/** Access hook for the unified /settings page — derives visibility from capabilities. */

import { useCapabilities } from '../../app/providers/CapabilitiesProvider';
import { useAgentCatalog } from '../../app/providers/AgentCatalogProvider';
import type { AgentView } from '@/entities/agent';

export interface SettingsAccess {
  isAdmin: boolean;
  visibleAgents: AgentView[];
  canAccessAdmin: boolean;
  canAccessAgent: (agentId: string) => boolean;
  /** Plugins is discoverable for every authenticated user. Individual
   *  plugins still own their feature flags inside the page. */
  canAccessPlugins: boolean;
  hasAnyAccess: boolean;
}

export function useSettingsAccess(): SettingsAccess {
  const { capabilities } = useCapabilities();
  const { agents } = useAgentCatalog();
  const isAdmin = capabilities?.is_admin === true;
  // Settings visibility must match what the owner APIs will actually
  // authorize. ``owned_agents`` is the authoritative list of agents the
  // caller has an explicit owner role for in user_roles.
  const owned = new Set<string>(capabilities?.owned_agents ?? []);
  const visibleAgents = isAdmin ? [...agents] : agents.filter((a) => owned.has(a.id));
  const canAccessAdmin = isAdmin;
  const canAccessAgent = (agentId: string): boolean => isAdmin || owned.has(agentId);
  const canAccessPlugins = true;
  const hasAnyAccess = canAccessAdmin || visibleAgents.length > 0 || canAccessPlugins;
  return {
    isAdmin,
    visibleAgents,
    canAccessAdmin,
    canAccessAgent,
    canAccessPlugins,
    hasAnyAccess,
  };
}
